/**
 * 🧊 **3D 를 A-Frame 무대에 세우는 손** — 카메라 AR(`MindArStage`)과 책 뷰어(`BookModelStage`)가 **같은 손**을 쓴다.
 *
 * ## 왜 한 파일인가 (2026-09-13)
 * 카메라에만 있던 3D 를 책 뷰어에도 세우면서 「엔티티 만들기·크기 재기·조명·그림자·각도/배율 갱신」이 두 벌이 될 뻔했다.
 * 두 벌이면 한쪽만 고쳐진다(잡으면 튀던 결함이 그 모양이었다). 값 계산은 `magicModel.ts`(순수), 판정은 shared,
 * 여기는 **DOM 에 붙이는 일**만.
 *
 * 🔴 A-Frame 은 `setAttribute` 를 가로챈다 — 컴포넌트에 넣고 DOM 속성에는 안 쓴다. 그래서 「지금 어떤 3D 가 서 있나」는
 *    우리가 붙인 `data-magic-model` 로 읽고, 「지금 몇 도인가」는 `object3D` 에서 읽는다(`turnFromModelRotation`).
 * 🔴 three 는 A-Frame 이 전역(`AFRAME.THREE`)으로 들고 있다 — 웹은 three 를 직접 의존하지 않으므로 모양만 적는다.
 */
import { MODEL_LIGHTS, gltfModelAttr, modelEntityAttrs, modelFit, turnFromModelRotation, type MagicModelReq } from "./magicModel";

type V3 = { x: number; y: number; z: number; set(x: number, y: number, z: number): void };
type ThreeLike = { Box3: new () => { setFromObject(o: unknown): { getSize(v: V3): V3; getCenter(v: V3): V3 } }; Vector3: new () => V3 };
type ModelEl = HTMLElement & {
  getObject3D?: (k: string) =>
    | {
        position: V3;
        parent?: { remove(o: unknown): void; add(o: unknown): void } | null;
        updateMatrixWorld?: (force?: boolean) => void;
        traverse?: (fn: (o: { castShadow?: boolean; receiveShadow?: boolean }) => void) => void;
      }
    | undefined;
  object3D?: { rotation: { y: number } };
};

/** 3D 엔티티를 찾는 표시 — 검사(`ar:walk`)도 이 이름으로 읽는다. */
export const MODEL_ATTR = "data-magic-model";
/** 크기를 재기 전엔 안 보이게 — 거대한 채 번쩍이지 않게. */
const HIDDEN_SCALE = "0.0001 0.0001 0.0001";

export interface MountOpts {
  /** 「움직임 줄이기」 — 저절로 안 돈다 */
  reducedMotion: boolean;
  /** 3D 를 못 불러왔다 → 화면이 띠로 돌아간다 */
  onFailed: () => void;
}

/**
 * 부모 엔티티 밑에 3D 를 **세우거나 갱신**한다. 주소가 바뀌면 새로, 같으면 각도·배율만.
 * `req` 가 없으면 치운다. 조명·그림자 판은 처음 세울 때 한 번만 붙인다(`data-magic-light`).
 */
export function mountModel(parent: Element, req: MagicModelReq | null, opts: MountOpts): void {
  let el = parent.querySelector<ModelEl>(`[${MODEL_ATTR}]`);
  if (!req) {
    el?.remove();
    parent.querySelector("[data-magic-light]")?.remove();
    return;
  }
  const attrs = modelEntityAttrs(req.turn, req.auto, req.frames, opts.reducedMotion, req.tilt, req.zoom);
  if (!parent.querySelector("[data-magic-light]")) parent.appendChild(makeLights());
  if (!el || el.getAttribute(MODEL_ATTR) !== req.url) {
    el?.remove();
    const made = document.createElement("a-entity") as ModelEl;
    made.setAttribute(MODEL_ATTR, req.url);
    made.setAttribute("gltf-model", gltfModelAttr(req.url));
    made.setAttribute("scale", HIDDEN_SCALE);
    made.setAttribute("shadow", "cast: true; receive: false");
    made.addEventListener("model-loaded", () => {
      // 실제 크기를 재서 책 폭에 맞추고, 모델의 가운데를 회전축으로(원점이 구석이면 돌 때 휘청인다)
      const THREE = (window as unknown as { AFRAME?: { THREE?: ThreeLike } }).AFRAME?.THREE;
      const mesh = made.getObject3D?.("mesh");
      if (!THREE || !mesh) return;
      /**
       * 🔴 **엔티티에서 떼어 놓고 잰다** (2026-09-13 · 관리자 미리보기를 진짜 크롬으로 걸어 보고 찾았다).
       * `setFromObject` 는 **세상 좌표**로 잰다. 붙인 채 재면 「재기 전엔 안 보이게」 준 배율(0.0001)과 카메라 AR 표지의
       * 배율까지 곱해진다. 수막새(0.155)가 0.0000155 로 재져 **만 배 크게** 섰고, 카메라가 물건 속에 들어가
       * 화면엔 「🧊 실물 3D」 표시만 있고 아무것도 안 보였다. 각도·배율 **문자열**만 보던 검사는 전부 초록이었다.
       * 떼어 놓으면 모델 자신의 좌표(= 엔티티 안 좌표)로 재진다. 같은 틱에 다시 붙인다(그 사이 그리지 않는다).
       */
      const holder = mesh.parent ?? null;
      holder?.remove(mesh);
      mesh.updateMatrixWorld?.(true);
      const box = new THREE.Box3().setFromObject(mesh);
      holder?.add(mesh);
      const size = box.getSize(new THREE.Vector3());
      const center = box.getCenter(new THREE.Vector3());
      mesh.position.set(-center.x, -center.y, -center.z);
      mesh.traverse?.((o) => {
        o.castShadow = true;
      });
      made.dataset.sizeX = String(size.x);
      made.dataset.sizeY = String(size.y);
      made.dataset.sizeZ = String(size.z);
      applyFit(made, Number(made.dataset.zoom ?? 1));
    });
    made.addEventListener("model-error", opts.onFailed);
    parent.appendChild(made);
    el = made;
  }
  el.setAttribute("rotation", attrs.rotation);
  if (attrs.animation) el.setAttribute("animation", attrs.animation);
  else el.removeAttribute("animation");
  // 🧊 배율 — 크기를 재기 전이면 기억만 해 두고, 잰 뒤(`model-loaded`)에 적용한다
  if (el.dataset.zoom !== String(attrs.zoom)) {
    el.dataset.zoom = String(attrs.zoom);
    if (el.dataset.sizeX !== undefined) applyFit(el, attrs.zoom);
  }
}

/** 잰 크기 × 배율 → scale·position. 크기를 아직 안 쟀으면 아무것도 안 한다. */
function applyFit(el: ModelEl, zoom: number): void {
  const size = { x: Number(el.dataset.sizeX), y: Number(el.dataset.sizeY), z: Number(el.dataset.sizeZ) };
  if (![size.x, size.y, size.z].every(Number.isFinite)) return;
  const fit = modelFit(size, zoom);
  el.setAttribute("scale", fit.scale);
  el.setAttribute("position", fit.position);
}

/** 💡 조명 + 그림자 받는 투명 판 — 부모(책 면 좌표계) 밑에 한 번. */
function makeLights(): Element {
  const g = document.createElement("a-entity");
  g.setAttribute("data-magic-light", "");
  const ambient = document.createElement("a-entity");
  ambient.setAttribute("light", MODEL_LIGHTS.ambient);
  const key = document.createElement("a-entity");
  key.setAttribute("light", MODEL_LIGHTS.key);
  key.setAttribute("position", MODEL_LIGHTS.keyPosition);
  // 책 면(z=0)에 그림자만 받는 판 — 색은 없다. 살짝 앞(0.002)에 둬 종이와 겹쳐 깜빡이지 않게
  const ground = document.createElement("a-plane");
  ground.setAttribute("width", "2");
  ground.setAttribute("height", "2");
  ground.setAttribute("position", "0 0 0.002");
  ground.setAttribute("material", MODEL_LIGHTS.ground);
  ground.setAttribute("shadow", "cast: false; receive: true");
  g.append(ambient, key, ground);
  return g;
}

/**
 * 🔄 **지금 3D 가 실제로 서 있는 각도**(0~1) — 저절로 돌던 것을 아이가 **잡을 때** 이어받으려고 묻는다.
 * A-Frame 의 애니는 속성이 아니라 `object3D` 를 돌리므로 **거기서 읽어야** 참값이다.
 * 없으면 `null` — 그때 아무것도 안 이어받는다(억지로 0 을 주면 정면으로 튄다).
 */
export function modelTurnNow(parent: Element | null | undefined): number | null {
  const el = parent?.querySelector<ModelEl>(`[${MODEL_ATTR}]`);
  const y = el?.object3D?.rotation.y;
  return typeof y === "number" ? turnFromModelRotation(y) : null;
}
